import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getColSpanClass(col: number | undefined) {
  switch (col) {
    case 6:
      return "col-span-6";
    case 4:
      return "col-span-4";
    case 3:
      return "col-span-3";
    case 8:
      return "col-span-8";
    case 12:
    default:
      return "col-span-12";
  }
}

export const isBrowser = () => {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
};

export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      if (isBrowser()) {
        return localStorage.getItem(key);
      }
      return null;
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      if (isBrowser()) {
        localStorage.setItem(key, value);
      }
    } catch {
      // Silently fail but log in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to set localStorage item:', { key, value });
      }
    }
  },
  removeItem: (key: string): void => {
    try {
      if (isBrowser()) {
        localStorage.removeItem(key);
      }
    } catch {
      // Silently fail but log in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to remove localStorage item:', key);
      }
    }
  }
};

export const getCacheClear = {
  gcTime: 0,            // Discard immediately after unmount
  staleTime: 0,         // Never considered fresh
  refetchOnMount: true, // Always refetch on mount
}

export const formatDateTime = (isoString: string) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(",", "");
};


export const getGridColumnClass = (col: number) => {
  const colMap: { [key: number]: string } = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7',
    8: 'col-span-8',
    9: 'col-span-9',
    10: 'col-span-10',
    11: 'col-span-11',
    12: 'col-span-12'
  };
  return colMap[col] || 'col-span-6';
};


/**
 * Convert a workflow-stage status code (CamelCase or snake_case) into a
 * human-readable label.  Backend emits stage names like "DataEntry",
 * "QualityReview", "MedicalReview"; the UI shows them as "Data Entry",
 * "Quality Review", "Medical Review" in stage columns, breadcrumbs, and
 * gating messages.  Returns "—" for empty/null input.
 */
export function humanizeWorkflowStage(raw: string | null | undefined): string {
  if (!raw) return "—";
  return raw
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
}

export function toCamelKey(key: string): string {
  return key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function toSnakeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, "_$1")
    .replace(/__/g, "_")
    .toLowerCase();
}

export function keysToCamel<T = any>(input: any): T {
  if (Array.isArray(input)) return input.map(keysToCamel) as any;
  if (input && typeof input === "object") {
    const out: any = {};
    Object.keys(input).forEach((k) => {
      const v = (input as any)[k];
      out[toCamelKey(k)] = keysToCamel(v);
    });
    return out as T;
  }
  return input as T;
}

export function keysToSnake<T = any>(input: any): T {
  if (Array.isArray(input)) return input.map(keysToSnake) as any;
  if (input && typeof input === "object") {
    const out: any = {};
    Object.keys(input).forEach((k) => {
      const v = (input as any)[k];
      out[toSnakeKey(k)] = keysToSnake(v);
    });
    return out as T;
  }
  return input as T;
}

export function parseInitialNumber(input: unknown): string {
  // Treat null/undefined as error
  if (input === null || input === undefined) {
    console.error('Value is required');
  }

  // Normalize to string
  const raw = String(input).trim().toLowerCase();

  if (raw === 'initial') {
    return "0"
  }

  if (raw === '0') {
    return "0";
  }

  // 3) other numbers -> keep as string (if valid)
  const num = Number(raw);

  // Not a number -> error
  if (Number.isNaN(num)) {
    console.error('Invalid number format');
  }

  // Optional: safety check for crazy big numbers
  if (!Number.isSafeInteger(num)) {
    console.error('Number is out of safe range');
  }

  // Return as string dynamically
  return raw;
}

dayjs.extend(utc);
dayjs.extend(customParseFormat);

const FORMAT_LIST = [
  "DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD",
  "DD-MM-YYYY", "DD/MM/YY", "MM/DD/YY",
  "HH:mm", "HH:mm:ss",
  "DD-MM-YYYY HH:mm", "DD-MM-YYYY HH:mm:ss",
  "DD/MM/YYYY HH:mm", "DD/MM/YYYY HH:mm:ss",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss",
  "HHmmss"
];


export function formatToGMT(input: any, includeTime: boolean = true) {
  if (!input) return "-";

  // Check if input is already in a partial date format (YYYY, MMM-YYYY, DD-MMM-YYYY)
  // Preserve these formats as-is to avoid converting "JAN-2000" to "01-JAN-2000"
  if (typeof input === 'string') {
    const trimmed = input.trim();
    
    // Format 1: YYYY (year only) - e.g., "2000"
    const yearOnlyPattern = /^\d{4}$/;
    if (yearOnlyPattern.test(trimmed)) {
      return trimmed;
    }
    
    // Format 2: MMM-YYYY (month-year) - e.g., "JAN-2000" or "jan-2000"
    const monthYearPattern = /^([A-Z]{3})-(\d{4})$/i;
    const monthYearMatch = trimmed.match(monthYearPattern);
    if (monthYearMatch) {
      const month = monthYearMatch[1].toUpperCase();
      const year = monthYearMatch[2];
      return `${month}-${year}`;
    }
    
    // Format 3: DD-MMM-YYYY (full date) - e.g., "01-JAN-2000" or "1-jan-2000"
    const fullDatePattern = /^(\d{1,2})-([A-Z]{3})-(\d{4})$/i;
    const fullDateMatch = trimmed.match(fullDatePattern);
    if (fullDateMatch) {
      const day = fullDateMatch[1].padStart(2, '0'); // Ensure 2-digit day
      const month = fullDateMatch[2].toUpperCase();
      const year = fullDateMatch[3];
      return `${day}-${month}-${year}`;
    }
  }

  const isDateObject = input instanceof Date;

  let parsed = dayjs(input, FORMAT_LIST, true);
  if (!parsed.isValid()) parsed = dayjs(input);
  if (!parsed.isValid()) return "Invalid date/time format";

  const hasDate = parsed.isValid();
  let hasTime = false;

  if (isDateObject) {
    // Treat 00:00:00 as date-only
    hasTime =
      input.getHours() !== 0 ||
      input.getMinutes() !== 0 ||
      input.getSeconds() !== 0;
  } else {
    const str = input.toString();
    hasTime = /(\d{2}:\d{2})|(\d{6})|T\d{2}:\d{2}/.test(str);
  }

  // Convert to UTC only if time exists
  if (hasTime) {
    parsed = parsed.utc();
  }

  if (hasDate && hasTime && includeTime) return parsed.format("DD-MMM-YYYY HH:mm:ss");
  if (hasDate && !hasTime) return parsed.format("DD-MMM-YYYY");
  if (hasDate && hasTime && !includeTime) return parsed.format("DD-MMM-YYYY");
  if (!hasDate && hasTime && includeTime) return parsed.format("HH:mm:ss");

  return "-";
}

export const getMedicallyConfirmDisplayValue = (value: string | number | boolean) => {
  switch (String(value)) {
    case "1":
    case "significant":
    case "true":
    case "yes":
      return "Yes";
    case "2":
    case "not_significant":
    case "no":
    case "false":
      return "No";
    case "3":
    case "unknown":
      return "-";
    default:
      return "-";
  }
};